import { useEffect, useRef } from "react";
import mqtt, { MqttClient } from "mqtt";
import { MQTT_WS_URL } from "../app/constants/config.constant";

export type EmployeePresence = {
  attendanceDate: string | null;
  checkIn: string | null;
  checkOut: string | null;
  isOnSite: boolean;
};

export type EmployeePresenceRecord = {
  _id: string;
  code: string;
  fullName: string;
  email?: string;
  phone?: string;
  avatar?: string;
  /** Display photo path (separate from FaceID avatar). */
  profilePhoto?: string | null;
  profilePhotoUrl?: string | null;
  hiddenFromReport?: boolean;
  team?: { name?: string };
  position?: { name?: string };
  presence: EmployeePresence;
};

export type PresenceMqttPayload = {
  type: "presence_update";
  action?: "checkin" | "checkout";
  date?: string;
  time?: string;
  employee: EmployeePresenceRecord;
  receivedAt?: string;
};

type UseEmployeePresenceMqttOptions = {
  mqttTopic?: string | null;
  enabled?: boolean;
  onUpdate: (payload: PresenceMqttPayload) => void;
};

export const useEmployeePresenceMqtt = ({
  mqttTopic,
  enabled = true,
  onUpdate,
}: UseEmployeePresenceMqttOptions) => {
  const onUpdateRef = useRef(onUpdate);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (!enabled || !mqttTopic || !MQTT_WS_URL) {
      return undefined;
    }

    let client: MqttClient | null = null;
    let disposed = false;

    client = mqtt.connect(MQTT_WS_URL, {
      reconnectPeriod: 5000,
      connectTimeout: 10000,
      clean: true,
    });

    client.on("connect", () => {
      if (disposed || !client) {
        return;
      }
      client.subscribe(mqttTopic, { qos: 1 });
    });

    client.on("message", (_topic, message) => {
      try {
        const payload = JSON.parse(message.toString()) as PresenceMqttPayload;
        if (payload?.type === "presence_update" && payload.employee?._id) {
          onUpdateRef.current(payload);
        }
      } catch (error) {
        console.error("[presence-mqtt] invalid payload", error);
      }
    });

    client.on("error", (error) => {
      console.error("[presence-mqtt] connection error", error.message);
    });

    return () => {
      disposed = true;
      client?.end(true);
    };
  }, [enabled, mqttTopic]);
};

export const isTodayPresence = (
  presence: EmployeePresence | undefined,
  presenceDate: string | null
) => !presenceDate || presence?.attendanceDate === presenceDate;

export const sortEmployeesByCheckInAsc = (
  employees: EmployeePresenceRecord[]
) => {
  return [...employees].sort((left, right) => {
    const leftCheckIn = left.presence?.checkIn;
    const rightCheckIn = right.presence?.checkIn;

    if (!leftCheckIn && !rightCheckIn) {
      return String(left.code).localeCompare(String(right.code));
    }
    if (!leftCheckIn) {
      return 1;
    }
    if (!rightCheckIn) {
      return -1;
    }

    const toSeconds = (time: string) => {
      const [hour = 0, minute = 0, second = 0] = time.split(":").map(Number);
      return hour * 3600 + minute * 60 + second;
    };

    const diff = toSeconds(leftCheckIn) - toSeconds(rightCheckIn);
    if (diff !== 0) {
      return diff;
    }

    return String(left.code).localeCompare(String(right.code));
  });
};

export const mergePresenceUpdate = (
  employees: EmployeePresenceRecord[],
  payload: PresenceMqttPayload,
  presenceDate?: string | null
) => {
  if (
    presenceDate &&
    ((payload.date && payload.date !== presenceDate) ||
      (payload.employee?.presence?.attendanceDate &&
        payload.employee.presence.attendanceDate !== presenceDate))
  ) {
    return employees;
  }

  const updatedEmployee = payload.employee;
  const exists = employees.some((item) => item._id === updatedEmployee._id);

  const nextEmployees = exists
    ? employees.map((item) =>
        item._id === updatedEmployee._id ? updatedEmployee : item
      )
    : [...employees, updatedEmployee];

  return sortEmployeesByCheckInAsc(nextEmployees);
};
